import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  IconButton,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import ContentCopy from '@mui/icons-material/ContentCopy';
import { Bookmark } from '../types/bookmarks';
import { useBookmarksModel } from '../hooks/useBookmarksModel';
import {
  useHighlightScroll,
  blockPulseHighlight,
} from '../hooks/useHighlightScroll';
import { normalizeUrl } from '../utils/bookmarkUtils';
import { ConfirmDialog } from './ConfirmDialog';
import BookmarkActionsMenu from './BookmarkActionsMenu';
import RichTextEditor from './RichTextEditor';

interface BookmarkCardProps {
  bookmark: Bookmark;
  isEditing: boolean;
  isNew?: boolean;
  highlighted?: boolean;
  onHighlightComplete?: () => void;
  onEditStart: () => void;
  onEditEnd: () => void;
  onDelete?: () => void;
  onClick?: () => void;
}

const BookmarkCard: React.FC<BookmarkCardProps> = ({
  bookmark,
  isEditing,
  isNew,
  highlighted,
  onHighlightComplete,
  onEditStart,
  onEditEnd,
  onDelete,
  onClick,
}) => {
  const { ref: cardRef } = useHighlightScroll<HTMLDivElement>(
    highlighted,
    onHighlightComplete,
  );

  const {
    editName,
    editUrl,
    editNotes,
    nameError,
    confirmDeleteOpen,
    notesExpanded,
    loading,
    isOnline,
    plainTextNotes,
    setEditName,
    setEditUrl,
    setEditNotes,
    setNotesExpanded,
    handleEditStart,
    handleSave,
    handleDeleteClick,
    handleConfirmDelete,
    handleCancelDelete,
    handleCopyUrl,
  } = useBookmarksModel({ bookmark, isNew, onEditStart, onEditEnd, onDelete });

  if (isEditing) {
    return (
      <>
        <Card variant="outlined" sx={{ mb: 1 }}>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}
            >
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                {isNew ? 'New Bookmark' : 'Edit Bookmark'}
              </Typography>
              <IconButton
                size="small"
                onClick={onEditEnd}
                aria-label="Cancel editing"
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
            <TextField
              label="Name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              fullWidth
              size="small"
              error={!!nameError}
              helperText={nameError}
              sx={{ mb: 1.5 }}
            />
            <TextField
              label="URL"
              value={editUrl}
              onChange={(e) => setEditUrl(e.target.value)}
              fullWidth
              size="small"
              sx={{ mb: 1.5 }}
            />
            <Box sx={{ mb: 2 }}>
              <RichTextEditor
                initialContent={editNotes}
                onChange={setEditNotes}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                size="small"
                variant="contained"
                disabled={!isOnline || loading}
                onClick={handleSave}
                startIcon={
                  loading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : undefined
                }
              >
                {loading ? 'Saving…' : 'Save'}
              </Button>
              {isNew && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  disabled={!isOnline}
                  onClick={handleDeleteClick}
                >
                  Delete
                </Button>
              )}
              <IconButton
                size="small"
                onClick={handleCopyUrl}
                aria-label="Copy URL"
              >
                <ContentCopy fontSize="small" />
              </IconButton>
            </Box>
          </CardContent>
        </Card>

        <ConfirmDialog
          open={confirmDeleteOpen}
          title="Delete Bookmark"
          message={`Are you sure you want to delete "${bookmark.bookmark_name}"?`}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      </>
    );
  }

  return (
    <Card
      ref={cardRef}
      variant="outlined"
      onClick={onClick}
      sx={{
        mb: 1,
        cursor: onClick ? 'pointer' : undefined,
        '&:hover': onClick ? { bgcolor: 'action.hover' } : undefined,
        ...(highlighted && {
          animation: `${blockPulseHighlight} 0.9s ease-in-out`,
          borderRadius: 1,
        }),
      }}
    >
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 }, minHeight: 48 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {normalizeUrl(bookmark.bookmark_url) ? (
              <Link
                href={normalizeUrl(bookmark.bookmark_url)!}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                variant="body1"
                sx={{ fontWeight: 600, fontSize: '1.05rem' }}
                onClick={(e) => e.stopPropagation()}
              >
                {bookmark.bookmark_name}
              </Link>
            ) : (
              <Typography
                variant="body1"
                component="span"
                sx={{ fontWeight: 600, fontSize: '1.05rem' }}
              >
                {bookmark.bookmark_name}
              </Typography>
            )}
            {plainTextNotes && (
              <Box sx={{ mt: 0.5 }}>
                {!notesExpanded && (
                  <Box
                    sx={{
                      maxHeight: 60,
                      overflow: 'hidden',
                      position: 'relative',
                      color: '#999',
                      fontStyle: 'italic',
                      '& p': { margin: 0 },
                      '& *': {
                        fontSize: '0.875rem',
                        color: '#999',
                        fontStyle: 'italic',
                      },
                    }}
                    dangerouslySetInnerHTML={{
                      __html: bookmark.bookmark_notes,
                    }}
                  />
                )}
                {!notesExpanded && (
                  <Button
                    size="small"
                    variant="text"
                    onClick={(e) => {
                      e.stopPropagation();
                      setNotesExpanded(true);
                    }}
                    sx={{
                      ml: 0.5,
                      minWidth: 'auto',
                      textTransform: 'none',
                      p: 0,
                    }}
                  >
                    [▼ more]
                  </Button>
                )}
                <Collapse in={notesExpanded}>
                  <Box
                    sx={{ mt: 0.5 }}
                    dangerouslySetInnerHTML={{
                      __html: bookmark.bookmark_notes,
                    }}
                  />
                  <Button
                    size="small"
                    variant="text"
                    onClick={(e) => {
                      e.stopPropagation();
                      setNotesExpanded(false);
                    }}
                    sx={{ minWidth: 'auto', textTransform: 'none', p: 0 }}
                  >
                    [▲ less]
                  </Button>
                </Collapse>
              </Box>
            )}
          </Box>
          <Box
            sx={{ display: 'flex', alignItems: 'flex-start', ml: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <BookmarkActionsMenu
              bookmark={bookmark}
              onEdit={handleEditStart}
              iconSize={20}
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default BookmarkCard;
